import {
  Box,
  Card,
  CardBody,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import {
  Populater,
  useGetProductGroupsAsSupplierQuery,
  type BidV2Dto,
  type BidV2PatchDto,
  type NumberFieldDto,
  type ProductFieldDto,
  type ProductGroupBidDto,
  type ProductTemplateFieldDto,
  type SupplierProductGroupDto,
} from "../../../../autogen/bff-api";
import { useEditor } from "../../../../common/editing/save-coordinator";
import { formatNumber } from "../../../../common/formatting/formatNumber";
import { BidV2ProductTables, toBidProductGroups } from "../../../../common/products/BidV2ProductTables";
import { NumericInput } from "../../../../common/input/NumericInput";
import { productBidTotal } from "../../../../common/products/productBidTotal";

const initiateUnitPrice = (): NumberFieldDto => ({
  type: "Number",
  id: uuid(),
  name: "Unit Price",
  isRequired: false,
  populatedBy: Populater.Supplier,
  value: undefined,
});

// A column header like "CO2 per m2" wraps to three lines and squeezes its column, leaving an input
// too narrow for the number it is meant to hold. These floors keep the fields legible regardless.
const SUPPLIER_FIELD_MIN_WIDTH = "6rem";
const UNIT_PRICE_MIN_WIDTH = "8rem";

const fieldDisplayValue = (field: ProductFieldDto | undefined): string => {
  if (field == null || field.value == null) return "—";
  if (field.type === "Boolean") return field.value ? "✓" : "—";
  return String(field.value);
};

export const BidProductsSection = ({
  bidId,
  eventId,
  readOnly,
}: {
  bidId: string;
  eventId: string;
  readOnly: boolean;
}) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<BidV2Dto, BidV2PatchDto>(bidId);
  const { data } = useGetProductGroupsAsSupplierQuery({ sourcingEventId: eventId });
  const groups = data?.productGroups ?? [];

  const bidProducts = state.products ?? [];
  const bidFor = (groupId: string, productId: string) =>
    bidProducts
      .find((group) => group.productGroupId === groupId)
      ?.productBids.find((product) => product.productId === productId);

  const rebuildWith = (
    groupId: string,
    productId: string,
    change: Partial<{ unitPrice: NumberFieldDto; productFields: ProductFieldDto[] }>,
  ): ProductGroupBidDto[] =>
    groups.map((group) => ({
      productGroupId: group.id,
      productBids: group.products.map((product) => {
        const existing = bidFor(group.id, product.id);
        const base = {
          productId: product.id,
          unitPrice: existing?.unitPrice ?? initiateUnitPrice(),
          productFields: existing?.productFields ?? [],
        };
        return group.id === groupId && product.id === productId ? { ...base, ...change } : base;
      }),
    }));

  const setUnitPrice = (groupId: string, productId: string, value: number | null) => {
    const unitPrice = { ...(bidFor(groupId, productId)?.unitPrice ?? initiateUnitPrice()), value: value ?? undefined };
    updateField({ products: rebuildWith(groupId, productId, { unitPrice }) }, { debounceMs: 400 });
  };

  const setSupplierField = (
    groupId: string,
    productId: string,
    template: ProductTemplateFieldDto,
    raw: string | boolean,
  ) => {
    const shared = {
      id: template.id,
      name: template.name,
      isRequired: template.isRequired,
      populatedBy: Populater.Supplier,
    };
    const field: ProductFieldDto =
      template.type === "Boolean"
        ? { ...shared, type: "Boolean", value: raw === true }
        : template.type === "Text"
          ? { ...shared, type: "Text", value: String(raw) }
          : { ...shared, type: "Number", value: raw === "" ? undefined : Number(raw) };
    const productFields = [
      ...(bidFor(groupId, productId)?.productFields ?? []).filter((f) => f.name !== template.name),
      field,
    ];
    updateField({ products: rebuildWith(groupId, productId, { productFields }) }, { debounceMs: 400 });
  };

  if (groups.length === 0) return null;
  const isSaving = fieldStatus("products") === "saving" || fieldStatus("products") === "pending";

  if (readOnly) {
    return (
      <Card variant="outline" rounded="xl">
        <CardBody>
          <Flex align="center" gap="2" mb="4">
            <Heading size="sm">{t("Products")}</Heading>
          </Flex>
          <BidV2ProductTables groups={toBidProductGroups(groups, bidProducts)} />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card variant="outline" rounded="xl">
      <CardBody>
        <Flex align="center" gap="2" mb="4">
          <Heading size="sm">{t("Products")}</Heading>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </Flex>
        {groups.map((group) => (
          <ProductGroupTable
            key={group.id}
            group={group}
            readOnly={readOnly}
            bidFor={(productId) => bidFor(group.id, productId)}
            onUnitPriceChange={(productId, value) => setUnitPrice(group.id, productId, value)}
            onSupplierFieldChange={(productId, template, raw) =>
              setSupplierField(group.id, productId, template, raw)
            }
          />
        ))}
        <Flex justify="flex-end" align="baseline" gap="2" pt="1">
          <Text fontSize="sm" color="smMuted">
            {t("Your total price")}
          </Text>
          <Text fontWeight="semibold">
            {formatNumber(productBidTotal(groups, bidProducts)) || "—"}
          </Text>
        </Flex>
      </CardBody>
    </Card>
  );
};

const ProductGroupTable = ({
  group,
  readOnly,
  bidFor,
  onUnitPriceChange,
  onSupplierFieldChange,
}: {
  group: SupplierProductGroupDto;
  readOnly: boolean;
  bidFor: (productId: string) => { unitPrice?: NumberFieldDto; productFields?: ProductFieldDto[] } | undefined;
  onUnitPriceChange: (productId: string, value: number | null) => void;
  onSupplierFieldChange: (productId: string, template: ProductTemplateFieldDto, raw: string | boolean) => void;
}) => {
  const { t } = useTranslation();
  const buyerFields = group.productFields.filter((field) => field.populatedBy === Populater.Buyer);
  const supplierFields = group.productFields.filter((field) => field.populatedBy === Populater.Supplier);

  return (
    <Box pb="6">
      <Text fontWeight="semibold" pb="2">
        {group.name}
      </Text>
      <Box border="1px solid" borderColor="smBorder" rounded="md" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              {buyerFields.map((field) => (
                <Th key={field.id}>{field.name}</Th>
              ))}
              <Th isNumeric>{t("Quantity")}</Th>
              {supplierFields.map((field) => (
                <Th key={field.id}>{field.name}</Th>
              ))}
              <Th isNumeric>{t("Unit price")}</Th>
              <Th isNumeric>{t("Sum")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {group.products.map((product) => {
              const bid = bidFor(product.id);
              const quantity = product.quantity.value ?? null;
              const unitPrice = bid?.unitPrice?.value ?? null;
              return (
                <Tr key={product.id}>
                  {buyerFields.map((field) => (
                    <Td key={field.id}>
                      {fieldDisplayValue(product.productFields.find((f) => f.name === field.name))}
                    </Td>
                  ))}
                  <Td isNumeric>{quantity ?? "—"}</Td>
                  {supplierFields.map((template) => {
                    const current = bid?.productFields?.find((f) => f.name === template.name);
                    return (
                      <Td key={template.id}>
                        {template.type === "Boolean" ? (
                          <Checkbox
                            isChecked={current?.type === "Boolean" ? current.value === true : false}
                            isDisabled={readOnly}
                            onChange={(e) => onSupplierFieldChange(product.id, template, e.target.checked)}
                          />
                        ) : template.type === "Number" ? (
                          <NumericInput
                            size="sm"
                            minW={SUPPLIER_FIELD_MIN_WIDTH}
                            numericValue={current?.type === "Number" ? current.value ?? null : null}
                            isDisabled={readOnly}
                            onNumericChange={(value) =>
                              onSupplierFieldChange(product.id, template, value == null ? "" : String(value))
                            }
                          />
                        ) : (
                          <Input
                            size="sm"
                            rounded="md"
                            minW={SUPPLIER_FIELD_MIN_WIDTH}
                            value={current?.type === "Text" ? current.value ?? "" : ""}
                            isDisabled={readOnly}
                            onChange={(e) => onSupplierFieldChange(product.id, template, e.target.value)}
                          />
                        )}
                      </Td>
                    );
                  })}
                  <Td isNumeric>
                    <NumericInput
                      size="sm"
                      minW={UNIT_PRICE_MIN_WIDTH}
                      numericValue={unitPrice}
                      isDisabled={readOnly}
                      onNumericChange={(value) => onUnitPriceChange(product.id, value)}
                    />
                  </Td>
                  <Td isNumeric>
                    {unitPrice != null ? formatNumber((quantity ?? 1) * unitPrice) : "—"}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
